import { Show } from "@refinedev/antd";
import { useShow, useNavigation } from "@refinedev/core";
import {
  Descriptions,
  Tag,
  Space,
  Card,
  Row,
  Col,
  Button,
  Timeline,
  Typography,
} from "antd";
import {
  DollarOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  UserOutlined,
  CheckCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import type { PlanRecord } from "@/lib/types";
import { brl, formatDate } from "@/lib/format";
import { StatCard } from "@/components/StatCard";

const { Text } = Typography;

const STATUS_COLOR: Record<PlanRecord["status"], string> = {
  active: "green",
  trialing: "blue",
  past_due: "orange",
  canceled: "red",
  none: "default",
};

const STATUS_LABEL: Record<PlanRecord["status"], string> = {
  active: "Ativa",
  trialing: "Trial",
  past_due: "Inadimplente",
  canceled: "Cancelada",
  none: "—",
};

/**
 * /admin/plans/:id — detalhe da assinatura (billing/Stripe).
 *
 * Como obtém os dados (MOCK MODE): `useShow` faz getOne em FIXTURES.plans. A
 * timeline de eventos é derivada dos campos existentes (criação, status atual,
 * próxima renovação) — quando a API real expuser o histórico de webhooks Stripe
 * (checkout, invoice.paid, customer.subscription.updated) basta plugar a fonte.
 * O link cruzado leva ao usuário dono (resource users).
 */
export const PlanShow: React.FC = () => {
  const { queryResult } = useShow<PlanRecord>();
  const { data, isLoading } = queryResult;
  const p = data?.data;
  const { show } = useNavigation();

  const mrr = (p?.mrr_cents ?? 0) / 100;

  return (
    <Show
      isLoading={isLoading}
      title="Assinatura"
      headerButtons={({ defaultButtons }) => (
        <Space>
          {defaultButtons}
          {p?.user_id && (
            <Button
              icon={<UserOutlined />}
              onClick={() => show("users", p.user_id)}
            >
              Ver usuário
            </Button>
          )}
        </Space>
      )}
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <StatCard
              title="MRR desta assinatura"
              value={brl.format(mrr)}
              prefix={<DollarOutlined />}
              hint={p ? STATUS_LABEL[p.status] : "—"}
            />
          </Col>
          <Col xs={24} sm={8}>
            <StatCard
              title="Receita anual"
              value={brl.format(mrr * 12)}
              prefix={<CalendarOutlined />}
              hint="MRR × 12"
            />
          </Col>
          <Col xs={24} sm={8}>
            <StatCard
              title="Tipo de cobrança"
              value={p?.tipo_cobranca ?? "—"}
              prefix={<CreditCardOutlined />}
              hint={p ? STATUS_LABEL[p.status] : ""}
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={14}>
            <Card className="mel-glass" bordered={false} title="Dados da assinatura">
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Usuário">
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    onClick={() => p?.user_id && show("users", p.user_id)}
                  >
                    {p?.email}
                  </Button>
                </Descriptions.Item>
                <Descriptions.Item label="Plano">
                  <Tag color={p?.plano === "premium" ? "gold" : "default"}>
                    {p?.plano}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={p ? STATUS_COLOR[p.status] : "default"}>
                    {p ? STATUS_LABEL[p.status] : "—"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="MRR">
                  {brl.format(mrr)}
                </Descriptions.Item>
                <Descriptions.Item label="Stripe customer">
                  {p?.stripe_customer_id ? (
                    <Text code>{p.stripe_customer_id}</Text>
                  ) : (
                    "—"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Stripe subscription">
                  {p?.stripe_subscription_id ? (
                    <Text code>{p.stripe_subscription_id}</Text>
                  ) : (
                    "—"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Próxima renovação">
                  {formatDate(p?.data_renovacao)}
                </Descriptions.Item>
                <Descriptions.Item label="Criada em">
                  {formatDate(p?.created_at)}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card
              className="mel-glass"
              bordered={false}
              title="Linha do tempo (billing)"
            >
              <Timeline
                items={[
                  {
                    color: "#06B6D4",
                    dot: <CheckCircleOutlined />,
                    children: (
                      <Space direction="vertical" size={0}>
                        <Text strong>Assinatura criada</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {formatDate(p?.created_at)} · checkout concluído
                        </Text>
                      </Space>
                    ),
                  },
                  {
                    color:
                      p?.status === "past_due"
                        ? "#F59E0B"
                        : p?.status === "canceled"
                          ? "#EF4444"
                          : "#10B981",
                    children: (
                      <Space direction="vertical" size={0}>
                        <Text strong>
                          Status atual: {p ? STATUS_LABEL[p.status] : "—"}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {p?.status === "past_due"
                            ? "Pagamento pendente — webhook invoice.payment_failed"
                            : p?.status === "canceled"
                              ? "Assinatura encerrada"
                              : "Pagamentos em dia"}
                        </Text>
                      </Space>
                    ),
                  },
                  ...(p?.data_renovacao
                    ? [
                        {
                          color: "#A5F3FC",
                          dot: <SyncOutlined />,
                          children: (
                            <Space direction="vertical" size={0}>
                              <Text strong>Próxima cobrança</Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {formatDate(p.data_renovacao)} ·{" "}
                                {brl.format(mrr)}
                              </Text>
                            </Space>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </Card>
          </Col>
        </Row>
      </Space>
    </Show>
  );
};
